import { tailorStage } from "@/lib/applications";
import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import MarkSentDialog from "@/components/applications/MarkSentDialog";
import SkipDialog from "@/components/applications/SkipDialog";
import Message from "@/components/common/Message";
import BeforeSending from "@/components/posting/BeforeSending";
import CheckLink from "@/components/posting/CheckLink";
import OwnApplication from "@/components/posting/OwnApplication";
import PostingBreadcrumb from "@/components/posting/PostingBreadcrumb";
import PostingButtons from "@/components/posting/PostingButtons";
import PostingHeading from "@/components/posting/PostingHeading";
import PostingSide from "@/components/posting/PostingSide";
import PostingText from "@/components/posting/PostingText";
import SentCard from "@/components/posting/SentCard";
import type { Application } from "@/lib/api/envoy";
import { usePostingDetail } from "@/lib/hooks/postingDetail";
import { useOpenTailored } from "@/lib/hooks/resume";
import { applicationChip, canSkip, isNoSuchPosting, offersSkip, postingFacts } from "@/lib/posting";
import {
    NO_SUCH_POSTING,
    payAndPlace,
    postingErrorLine,
    postingPageName,
    seenLine,
    sentSideOf,
    withSentSide,
} from "@/lib/posting/page";
import { pageTitle, usePageTitle } from "@/lib/pageTitle";
import { isUuid } from "@/lib/ids";
import { skippingOf, type Skipping } from "@/lib/skipping";
import { useAuth } from "@/auth/session";
import { useClock } from "@/lib/clock";
import { useGate } from "@/components/site/gateContext";
import { useStanding } from "@/lib/hooks/standingContext";
import { tailorTerms } from "@/lib/standing";
import { isCheckable } from "@/static/postingPage";

/**
 * One posting: what it says, how the person's resume fits it,
 * the resumes written for it and the record from the catalogue and from
 * envoy. Once applied, the resume that went leads, with what the tailor
 * changed, and there is nothing left to decide.
 *
 * For everyone: the whole posting and Apply on their site, and Save.
 * Signed in, the fit with the keywords missed and Tailor my resume, with
 * the line that says which writer and how many tailors are left.
 */
export default function PostingDetail() {
    const { postingId: given = "" } = useParams();
    // An id that is not one the catalogue gives asks nothing of anyone.
    const known = isUuid(given);
    const postingId = known ? given : "";
    const [params, setParams] = useSearchParams();
    const { state } = useAuth();
    const member = state.status === "signed-in";
    const standing = useStanding();
    const { ask } = useGate();
    const detail = usePostingDetail(postingId, known, member);
    const { posting, application, applied, sent, fit } = detail;
    const openPdf = useOpenTailored();
    const [sending, setSending] = useState<Application | null>(null);
    const [skipping, setSkipping] = useState<Skipping | null>(null);
    const { now, timeZone, locale } = useClock();
    usePageTitle(pageTitle(postingPageName(posting.data)));

    if (!known) return <Message>{NO_SUCH_POSTING}</Message>;
    if (posting.isPending) return <Message>Reading the posting…</Message>;
    if (posting.isError && isNoSuchPosting(posting.error)) {
        // A page posting has no catalogue row: the person's own application is its page.
        const waiting =
            state.status === "loading" || (member && !detail.answered && !detail.applicationFailed);
        if (waiting) return <Message>Reading the posting…</Message>;
        if (application) return <OwnApplication application={application} />;
    }
    if (posting.isError) return <Message>{postingErrorLine(posting.error)}</Message>;

    const data = posting.data;
    return (
        <div className="-mt-1 flex flex-col gap-5 text-ui">
            <PostingBreadcrumb posting={data} applied={applied} />

            <div className="flex flex-wrap items-start justify-between gap-4">
                <PostingHeading
                    posting={data}
                    applied={applied}
                    sent={sent}
                    chip={applicationChip(application, data)}
                    member={member}
                    seen={seenLine(data, applied, now, timeZone, locale)}
                    timeZone={timeZone}
                />
                <PostingButtons
                    posting={data}
                    application={application}
                    applied={applied}
                    sent={sent}
                    showSave={state.status !== "loading"}
                    showSkip={offersSkip(application)}
                    onOpenPdf={(resumeId) => openPdf.mutate(resumeId)}
                    onSent={setSending}
                    onSkip={() => setSkipping(skippingOf(application, data))}
                />
            </div>

            <div className="flex flex-wrap items-start gap-5">
                <div className="flex min-w-0 flex-[2_1_520px] flex-col gap-5">
                    {!member && isCheckable(data, now) && <CheckLink posting={data} />}
                    {sent ? (
                        <>
                            <SentCard
                                sent={sent}
                                written={detail.written.find(
                                    (entry) => entry.resumeId === sent.resumeId,
                                )}
                                link={detail.sentLink}
                                modelName={detail.modelName}
                                fit={fit}
                                view={sentSideOf(params)}
                                onView={(side) =>
                                    setParams(withSentSide(params, side), { replace: true })
                                }
                            />
                            <PostingText
                                description={data.description}
                                payAndPlace={payAndPlace(data)}
                            />
                        </>
                    ) : (
                        <BeforeSending
                            posting={data}
                            member={member}
                            atMatch={Boolean(application)}
                            fit={fit}
                            fitLoading={detail.fitLoading}
                            tailor={
                                member && !data.closed_at && canSkip(application)
                                    ? {
                                          stage: tailorStage(application ?? undefined),
                                          ...tailorTerms(standing),
                                          resumeId: application?.resume_id,
                                          failed: application?.status === "FAILED",
                                      }
                                    : null
                            }
                            written={detail.written}
                            onAddResume={() => ask("upload")}
                        />
                    )}
                </div>
                <PostingSide
                    facts={postingFacts(data, application, sent, timeZone)}
                    fitAtMatch={sent && fit && fit.asked > 0 ? fit : null}
                    scoredAt={detail.scoredAt}
                    member={member}
                    record={detail.record}
                    recordError={detail.recordError}
                />
            </div>

            <MarkSentDialog application={sending} onClose={() => setSending(null)} />
            <SkipDialog
                application={skipping?.application ?? null}
                posting={skipping?.posting ?? null}
                onClose={() => setSkipping(null)}
            />
        </div>
    );
}
